import { deriveDataCoverageConfidence } from "@/utils/analyticsConfidence";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const fmt = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-IN") : v ?? "—";

export const pct = (v, digits = 2) =>
  typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(digits)}%` : "—";

const studentsToFullCoverage = (overview) => {
  const total = overview?.total_enrolment ?? 0;
  const passed = overview?.aadhaar_passed ?? 0;
  return Math.max(0, total - passed);
};

export const deriveAadhaarInsightConfidence = (overview, index = 0) => {
  const filled = [
    overview?.total_enrolment > 0,
    typeof overview?.aadhaar_coverage_pct === "number",
    typeof overview?.aadhaar_exception_pct === "number",
    typeof overview?.name_mismatch_pct === "number",
    typeof overview?.mbu_pending_total_pct === "number",
  ].filter(Boolean).length;
  const base = deriveDataCoverageConfidence({
    totalUnits: 5,
    unitsWithData: filled,
    anchorMetric: overview?.aadhaar_coverage_pct ?? null,
  });
  if (base <= 0) return 0;
  return clamp(base - index * 3, 0, 96);
};

export const deriveAadhaarOverallConfidence = (overview, blockData = []) => {
  return deriveDataCoverageConfidence({
    totalUnits: Math.max(blockData.length, 1),
    unitsWithData: blockData.filter((b) => typeof b.aadhaar_coverage_pct === "number").length,
    anchorMetric: overview?.aadhaar_coverage_pct ?? null,
  });
};

export const countAadhaarPrioritySignals = (overview, blockData = []) => {
  let count = 0;
  if ((overview?.aadhaar_exception_pct ?? 0) >= 4) count += 1;
  if ((overview?.name_mismatch_pct ?? 0) >= 5) count += 1;
  if ((overview?.mbu_pending_total_pct ?? 0) >= 12) count += 1;
  const lowBlocks = blockData.filter(
    (b) => typeof b.aadhaar_coverage_pct === "number" && b.aadhaar_coverage_pct < 94
  ).length;
  if (lowBlocks > 0) count += 1;
  return count;
};

export const buildAadhaarInsightCards = (overview, blockData = []) => {
  const coverage = overview?.aadhaar_coverage_pct ?? 0;
  const mismatch = overview?.name_mismatch_pct ?? 0;
  const mbu = overview?.mbu_pending_total_pct ?? 0;
  const bottomBlocks = [...blockData]
    .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
    .slice(0, 4)
    .map((b) => b.block_name)
    .filter(Boolean);

  return [
    {
      id: "coverage",
      tone: "green",
      signal: "Positive",
      signalClass: "",
      iconClass: "green",
      title: "Coverage Base Is Strong",
      body:
        coverage >= 95
          ? "District coverage is above 95%, so the next improvement wave should shift from mass coverage to targeted exception closure."
          : `Coverage is ${pct(coverage)}; focus on closing the remaining ${fmt(studentsToFullCoverage(overview))} student records.`,
      confidence: deriveAadhaarInsightConfidence(overview, 0),
      barClass: "",
    },
    {
      id: "mismatch",
      tone: "red",
      signal: "Risk",
      signalClass: "red",
      iconClass: "red",
      title: "Name Mismatch Is the Leading Risk",
      body:
        mismatch >= 5
          ? "Mismatch risk is materially higher than failed/pending share and needs school-level document verification before upload retries."
          : `Name mismatch at ${pct(mismatch)}; monitor schools with demographic field discrepancies.`,
      confidence: deriveAadhaarInsightConfidence(overview, 1),
      barClass: "red",
    },
    {
      id: "mbu",
      tone: "purple",
      signal: "Bottleneck",
      signalClass: "purple",
      iconClass: "purple",
      title: "MBU Pending Is the Largest Workload",
      body: `MBU cases affect ${pct(mbu)} of enrolment (${fmt(overview?.mbu_pending_total ?? 0)} students), so camp scheduling and parent communication should be prioritized block-wise.`,
      confidence: deriveAadhaarInsightConfidence(overview, 2),
      barClass: "purple",
    },
    {
      id: "blocks",
      tone: "orange",
      signal: "Action",
      signalClass: "amber",
      iconClass: "orange",
      title: "Bottom Blocks Need a 7-Day Sprint",
      body: bottomBlocks.length
        ? `${bottomBlocks.join(", ")} should be reviewed as a focused closure cluster with daily progress tracking.`
        : "Identify lowest-coverage blocks and run a 7-day exception closure sprint.",
      confidence: deriveAadhaarInsightConfidence(overview, 3),
      barClass: "orange",
    },
  ];
};

export const buildAadhaarRootCauses = (overview) => {
  const mismatch = overview?.name_mismatch_pct ?? 0;
  const mbu = overview?.mbu_pending_total_pct ?? 0;
  const failed = overview?.aadhaar_failed_pct ?? 0;
  const pending = overview?.aadhaar_pending_pct ?? 0;
  const notProvided = overview?.aadhaar_not_provided_pct ?? 0;

  const rows = [
    { label: "Name Mismatch\nvalidation Gap", width: clamp(mismatch * 16, 20, 95), tone: "red", value: mismatch >= 5 ? "High" : "Medium" },
    { label: "MBU Camp\ndependency", width: clamp(mbu * 5.5, 20, 95), tone: "purple", value: mbu >= 12 ? "High" : "Medium" },
    { label: "School-Level\ndocument Mismatch", width: clamp((failed + notProvided) * 12, 15, 85), tone: "orange", value: failed >= 2 ? "Medium" : "Low" },
    { label: "Low Enrolment\nhigh Exception Schools", width: clamp((overview?.high_risk_schools ?? 0) > 50 ? 72 : 48, 15, 80), tone: "red", value: (overview?.high_risk_schools ?? 0) > 0 ? "Medium" : "Low" },
    { label: "System Upload\npending Queue", width: clamp(pending * 18, 10, 60), tone: "green", value: pending >= 1 ? "Low" : "Low" },
  ];
  return rows;
};

export const buildAadhaarRecommendations = (overview, blockData = []) => {
  const bottom = [...blockData]
    .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
    .slice(0, 5)
    .map((b) => b.block_name)
    .filter(Boolean);

  return [
    {
      num: 1,
      tone: "red",
      title: "Run Name-Mismatch Verification Desk",
      body: "Ask schools to verify student name, DOB and guardian details against Aadhaar before resubmission.",
    },
    {
      num: 2,
      tone: "purple",
      title: "Schedule MBU Camps by Backlog Density",
      body: `Prioritize blocks with high MBU pending (${pct(overview?.mbu_pending_total_pct ?? 0)}) and larger enrolment base, then move to smaller schools.`,
    },
    {
      num: 3,
      tone: "orange",
      title: "Create Bottom-Block Closure Sprint",
      body: bottom.length
        ? `Daily review of ${bottom.join(", ")} until coverage gap reduces.`
        : "Daily review of lowest-coverage blocks until gap reduces.",
    },
  ];
};

export const buildAadhaarExecutiveSummary = (overview) => {
  if (!overview?.total_enrolment) {
    return ["No Aadhaar data available for the selected scope."];
  }
  return [
    `State-wide Aadhaar coverage is ${pct(overview.aadhaar_coverage_pct)} across ${fmt(overview.total_enrolment)} students.`,
    `Exception rate is ${pct(overview.aadhaar_exception_pct)}, driven by failed records, name mismatch and not-provided cases.`,
    `MBU pending is ${pct(overview.mbu_pending_total_pct)}, representing the largest operational backlog (${fmt(overview.mbu_pending_total)} students).`,
  ];
};

export const buildAadhaarRootCauseSignals = (overview) => [
  `Name mismatch (${pct(overview?.name_mismatch_pct ?? 0)}): likely due to spelling variation, local language transliteration, DOB mismatch and guardian detail mismatch.`,
  `MBU pending (${pct(overview?.mbu_pending_total_pct ?? 0)}): likely due to biometric update dependency, parent availability and camp scheduling gaps.`,
  `Failed / not-provided (${fmt(overview?.aadhaar_failed ?? 0)} failed, ${fmt(overview?.aadhaar_not_provided ?? 0)} not provided): school-led collection and document validation gaps.`,
  `High-risk schools (${fmt(overview?.high_risk_schools ?? 0)}): small enrolment can inflate exception percentages — prioritize both count and rate.`,
];

export const buildAadhaarRecommendationBullets = (overview, blockData = []) => {
  const bottom = [...blockData]
    .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
    .slice(0, 5)
    .map((b) => b.block_name)
    .filter(Boolean);
  return [
    bottom.length
      ? `Launch a 7-day bottom-block closure sprint for ${bottom.join(", ")}.`
      : "Launch a 7-day bottom-block closure sprint for lowest-coverage blocks.",
    "Create a school validation desk for name mismatch before Aadhaar resubmission.",
    `Schedule MBU biometric update camps using backlog density (${fmt(overview?.mbu_pending_total ?? 0)} pending).`,
  ];
};

export const buildAadhaarPriorityActions = (overview = {}, highRiskSchools = []) => {
  const pending = overview.aadhaar_pending ?? overview.pending_total ?? 0;
  const failed = overview.aadhaar_failed ?? overview.failed_total ?? 0;
  const exceptions = overview.exception_schools ?? highRiskSchools.length;
  const topSchool = highRiskSchools[0];

  return [
    `Block Education Officer: publish daily closure list for ${fmt(exceptions)} high-exception school${exceptions === 1 ? "" : "s"}.`,
    `Cluster Officer: validate mismatch cases before retry (${fmt(pending)} pending records in scope).`,
    `Data Entry Operator: clear ${fmt(failed)} failed upload${failed === 1 ? "" : "s"} with reason-code tagging.`,
    ...(topSchool
      ? [`Priority school: ${topSchool.school_name} (${pct(topSchool.exception_rate ?? 0)} exceptions).`]
      : []),
  ];
};

const inferSchoolReason = (school) => {
  const enr = school.total_enrolment ?? 0;
  const rate = school.exception_rate ?? 0;
  if (enr <= 3 && rate >= 80) return "Small enrolment exception";
  if ((school.aadhaar_failed ?? 0) >= (school.aadhaar_pending ?? 0)) return "Failed validation";
  if (rate >= 70) return "Name mismatch cluster";
  if ((school.aadhaar_pending ?? 0) > 0) return "Pending correction";
  if ((school.aadhaar_not_provided ?? 0) > 0) return "Aadhaar not validated";
  return "High exception rate";
};

const inferSchoolAction = (school) => {
  const reason = inferSchoolReason(school);
  if (reason.includes("Small")) return "Verify all records";
  if (reason.includes("Failed")) return "Immediate resubmit";
  if (reason.includes("mismatch")) return "Parent validation desk";
  if (reason.includes("Pending")) return "Upload correction batch";
  if (reason.includes("not validated")) return "Document check";
  return "Daily closure report";
};

const inferSchoolOwner = (school) => {
  const enr = school.total_enrolment ?? 0;
  if (enr <= 3) return "School HM";
  if ((school.exception_rate ?? 0) >= 80) return "Cluster Officer";
  if ((school.aadhaar_pending ?? 0) > (school.aadhaar_failed ?? 0)) return "Data Operator";
  return "Block Officer";
};

export const buildAadhaarPrioritySchools = (highRiskSchools = []) =>
  highRiskSchools.slice(0, 8).map((school, idx) => {
    const rate = school.exception_rate ?? 0;
    const tagTone = rate >= 90 ? "" : "amber";
    const badgeTone = idx < 3 ? "" : "amber";
    return {
      priority: idx + 1,
      school: school.school_name || "—",
      block: school.block_name || "—",
      signal: pct(rate, rate >= 99 ? 0 : 2),
      signalTag: rate >= 99 ? "100% exception" : `${pct(rate)} exception`,
      tagTone,
      badgeTone,
      reason: inferSchoolReason(school),
      action: inferSchoolAction(school),
      owner: inferSchoolOwner(school),
    };
  });

export const formatCompact = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString("en-IN");
};

export const deriveRiskLoad = (overview) =>
  clamp(Math.round(overview?.aadhaar_exception_pct ?? 0), 0, 100);

const topBlockBy = (blockData, key, limit = 1) =>
  [...blockData]
    .sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0))
    .slice(0, limit)
    .map((b) => b.block_name)
    .filter(Boolean);

const relativeWidth = (value, max) => (max > 0 ? clamp((value / max) * 100, 8, 100) : 8);

const inferRiskLevel = (school) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 || (enr <= 3 && rate >= 80)) return { level: "P0", tone: "red" };
  if (rate >= 80) return { level: "P1", tone: "red" };
  return { level: "P2", tone: "amber" };
};

const inferOperationalOwner = (school, rank) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 && enr <= 3) {
    if (rank === 1) return "Block Officer";
    if (rank === 2) return "Cluster Head";
    return "School Head";
  }
  if (enr >= 100) return "Nodal Officer";
  if ((school.aadhaar_pending ?? 0) > (school.aadhaar_failed ?? 0)) return "Data Operator";
  if (enr <= 15) return "School Head";
  if (rate >= 80) return "Nodal Officer";
  return "Cluster Head";
};

const inferNextActionLink = (school, rank) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 && enr <= 3) {
    if (rank === 1) return "Verify today";
    if (rank === 2) return "Call school";
    return "Visit school";
  }
  if (enr >= 100) return "Bulk validation";
  if ((school.aadhaar_pending ?? 0) > 0) return "Assign case";
  if (rate >= 75) return "Correct records";
  if (enr <= 15) return "Fix Aadhaar";
  return "Review list";
};

export const buildPrimaryOperationalAction = (blockData = []) => {
  const topFailed = topBlockBy(blockData, "aadhaar_failed", 3);
  if (!topFailed.length) return "Identify highest-exception blocks and assign a 7-day closure sprint.";
  return `Assign ${topFailed.join(", ")} to a 7-day exception closure sprint with school-level follow-up.`;
};

export const buildControlPriorities = (overview, blockData = []) => {
  const failedMax = Math.max(...blockData.map((b) => b.aadhaar_failed ?? 0), 1);
  const notProvidedMax = Math.max(...blockData.map((b) => b.aadhaar_not_provided ?? 0), 1);
  const mismatchMax = Math.max(...blockData.map((b) => b.name_mismatch_pct ?? 0), 1);
  const mbuMax = Math.max(...blockData.map((b) => b.mbu_pending_pct ?? 0), 1);

  return [
    {
      id: "failed",
      title: "Failed Aadhaar Records",
      value: formatCompact(overview?.aadhaar_failed ?? 0),
      unit: "Records",
      iconClass: "red",
      dotClass: "red",
      barClass: "red",
      barWidth: relativeWidth(overview?.aadhaar_failed ?? 0, failedMax * blockData.length || failedMax),
      metaLeft: `Top block: ${topBlockBy(blockData, "aadhaar_failed")[0] || "—"}`,
      priority: "P1",
    },
    {
      id: "not-provided",
      title: "Not Provided Records",
      value: formatCompact(overview?.aadhaar_not_provided ?? 0),
      unit: "Records",
      iconClass: "",
      dotClass: "amber",
      barWidth: relativeWidth(overview?.aadhaar_not_provided ?? 0, notProvidedMax * blockData.length || notProvidedMax),
      metaLeft: "Largest issue",
      priority: "P1",
    },
    {
      id: "mismatch",
      title: "Name Mismatch Risk",
      value: pct(overview?.name_mismatch_pct ?? 0),
      unit: "District",
      iconClass: "orange",
      dotClass: "amber",
      barClass: "orange",
      barWidth: relativeWidth(overview?.name_mismatch_pct ?? 0, mismatchMax),
      metaLeft: `${topBlockBy(blockData, "name_mismatch_pct")[0] || "—"} high`,
      priority: "P2",
    },
    {
      id: "mbu",
      title: "MBU Pending",
      value: pct(overview?.mbu_pending_total_pct ?? 0),
      unit: "Students",
      iconClass: "purple",
      dotClass: "amber",
      barClass: "purple",
      barWidth: relativeWidth(overview?.mbu_pending_total_pct ?? 0, mbuMax),
      metaLeft: "Age 5–15 major",
      priority: "P2",
    },
  ];
};

export const buildStackChartRows = (blockData = [], limit = 10) => {
  const rows = [...blockData]
    .map((b) => {
      const failed = b.aadhaar_failed ?? 0;
      const pending = b.aadhaar_pending ?? 0;
      const notProvided = b.aadhaar_not_provided ?? 0;
      const total = failed + pending + notProvided;
      return { block: b.block_name, failed, pending, notProvided, total };
    })
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);

  return rows.map((r) => ({
    ...r,
    failedPct: r.total > 0 ? (r.failed / r.total) * 100 : 0,
    pendingPct: r.total > 0 ? (r.pending / r.total) * 100 : 0,
    notProvidedPct: r.total > 0 ? (r.notProvided / r.total) * 100 : 0,
  }));
};

export const buildWorkQueue = (overview, blockData = [], highRiskSchools = []) => {
  const topFailed = topBlockBy(blockData, "aadhaar_failed", 3);
  const highMismatchBlocks = blockData.filter((b) => (b.name_mismatch_pct ?? 0) >= 7).length;
  const p0Schools = highRiskSchools.filter(
    (s) => (s.exception_rate ?? 0) >= 99 || ((s.total_enrolment ?? 0) <= 3 && (s.exception_rate ?? 0) >= 80)
  ).length;

  return [
    {
      priority: "P1",
      tone: "red",
      title: "Close Failed Aadhaar Records",
      body: topFailed.length
        ? `${topFailed.join(", ")} contribute the maximum error load.`
        : "Focus on blocks with the highest failed record counts.",
      value: formatCompact(overview?.aadhaar_failed ?? 0),
      owner: "Block officer",
    },
    {
      priority: "P1",
      tone: "blue",
      title: "Collect Aadhaar Not Provided Cases",
      body: "Schedule school-level drive for missing Aadhaar records.",
      value: formatCompact(overview?.aadhaar_not_provided ?? 0),
      owner: "School head",
    },
    {
      priority: "P2",
      tone: "orange",
      title: "Resolve Name Mismatch",
      body: highMismatchBlocks
        ? `Focus on ${highMismatchBlocks} blocks above 7% mismatch and validate student records.`
        : "Validate student records in blocks with elevated mismatch rates.",
      value: pct(overview?.name_mismatch_pct ?? 0),
      owner: "Data operator",
    },
    {
      priority: "P2",
      tone: "purple",
      title: "Clear MBU Pending Backlog",
      body: "Age 5–15 students need faster verification closure.",
      value: pct(overview?.mbu_pending_total_pct ?? 0),
      owner: "Cluster head",
    },
    {
      priority: "P0",
      tone: "red",
      title: "Visit Schools with 100% Exception Rate",
      body: "Small enrolment schools with full exception load need immediate validation.",
      value: String(p0Schools),
      owner: "Field visit",
    },
  ];
};

export const buildMismatchRiskBars = (blockData = [], limit = 8) => {
  const rows = [...blockData]
    .sort((a, b) => (b.name_mismatch_pct ?? 0) - (a.name_mismatch_pct ?? 0))
    .slice(0, limit);
  const max = Math.max(...rows.map((b) => b.name_mismatch_pct ?? 0), 1);
  return rows.map((b) => {
    const score = b.name_mismatch_pct ?? 0;
    let tone = "amber";
    if (score >= 8) tone = "red";
    else if (score <= 4) tone = "green";
    return {
      block: b.block_name,
      score,
      width: relativeWidth(score, max),
      tone,
    };
  });
};

export const buildMbuAgeBars = (mbuData = [], limit = 8) => {
  const rows = [...mbuData].sort((a, b) => (b.mbu_total ?? 0) - (a.mbu_total ?? 0)).slice(0, limit);
  const max = Math.max(...rows.map((b) => b.mbu_total ?? 0), 1);
  return rows.map((b) => ({
    block: b.block_name,
    total: b.mbu_total ?? 0,
    mbu515: b.mbu_5_15 ?? 0,
    mbu15plus: b.mbu_15_plus ?? 0,
    width: relativeWidth(b.mbu_total ?? 0, max),
    label: formatCompact(b.mbu_total ?? 0),
  }));
};

export const buildOperationalSchoolRows = (highRiskSchools = [], limit = 20) =>
  highRiskSchools.slice(0, limit).map((school, idx) => {
    const rank = idx + 1;
    const risk = inferRiskLevel(school);
    const rate = school.exception_rate ?? 0;
    return {
      rank,
      school: school.school_name || "—",
      udiseCode: school.udise_code,
      block: school.block_name || "—",
      blockCode: school.block_code,
      enrolment: school.total_enrolment ?? 0,
      exceptionRate: pct(rate, rate >= 99 ? 0 : 2),
      exceptionTone: rate >= 80 ? "red" : "amber",
      coverage: pct(school.aadhaar_coverage_pct ?? 0),
      risk: risk.level,
      riskTone: risk.tone,
      owner: inferOperationalOwner(school, rank),
      nextAction: inferNextActionLink(school, rank),
    };
  });

export const buildAccountabilityCards = (blockData = []) => {
  const sprintBlocks = topBlockBy(blockData, "aadhaar_failed", 3);
  const blockLabel = sprintBlocks.length ? sprintBlocks.join(", ") : "top exception blocks";
  return [
    {
      title: "7-Day Closure Sprint",
      subtitle: "For Blocks with Highest Failed and Not-Provided Issue Load.",
      pill: "P1",
      pillTone: "red",
      iconClass: "red",
      actionTitle: "Assign block-wise exception owners",
      actionBody: `${blockLabel} get dedicated closure owners and daily progress tracking.`,
      successTitle: "Success measure",
      successBody: "Reduce open issue count by at least 25% in the first sprint.",
    },
    {
      title: "School Validation Drive",
      subtitle: "For Schools with Low Enrolment and 100% Exception Rate.",
      pill: "P0",
      pillTone: "amber",
      iconClass: "orange",
      actionTitle: "Validate source records",
      actionBody:
        "Check whether zero coverage is due to missing Aadhaar, wrong student records or data-entry delay.",
      successTitle: "Success measure",
      successBody: "Close all P0 schools within 72 hours with evidence of correction.",
    },
    {
      title: "MBU Backlog Review",
      subtitle: "For Age-Wise Pending Records and Verification Delay.",
      pill: "P2",
      pillTone: "",
      iconClass: "purple",
      actionTitle: "Separate age 5–15 and 15+ queues",
      actionBody:
        "Route age 5–15 cases to school teams and age 15+ cases to block-level verification desk.",
      successTitle: "Success measure",
      successBody: "Bring MBU pending below 10% and track weekly until stable.",
    },
  ];
};

export const buildAadhaarExecutiveMarkdown = (overview = {}, blockData = []) => {
  const triage = buildExecutiveTriageCards(overview);
  const queue = buildExecutiveActionQueueItems(blockData, []);

  return [
    "## Insights",
    `- ${buildExecutiveHeroNarrative(overview)}`,
    ...triage.map((c) => `- ${c.title}: ${c.body}`),
    "",
    "## Priority Action Items",
    `- ${buildPrimaryExecutiveAction(blockData)}`,
    ...queue.slice(0, 4).map((a) => `- ${a.title}: ${a.body}`),
  ].join("\n");
};

export const buildExecutiveHeroNarrative = (overview = {}) => {
  const coverage = overview.aadhaar_coverage_pct ?? 0;
  const mbu = overview.mbu_pending_total_pct ?? 0;
  const mismatch = overview.name_mismatch_pct ?? 0;
  if (coverage >= 95 && (mbu >= 12 || mismatch >= 5)) {
    return "Coverage is strong, but the next review should focus on MBU pending and name mismatch cleanup because those directly affect student identity quality.";
  }
  if (coverage >= 90) {
    return "Coverage is improving steadily. Prioritize exception closure in bottom blocks before the next monthly Aadhaar validation cycle.";
  }
  return "Coverage needs focused intervention. Run a block-wise closure sprint with daily exception tracking and school-level ownership.";
};

export const buildExecutiveTriageCards = (overview = {}) => {
  const failed = overview.aadhaar_failed_pct ?? 0;
  const mismatch = overview.name_mismatch_pct ?? 0;
  const mbu = overview.mbu_pending_total_pct ?? 0;
  const notProvided = overview.aadhaar_not_provided_pct ?? 0;
  return [
    {
      id: "failed",
      title: "Failed Validation",
      value: pct(failed),
      body: "Records rejected due to invalid format or verification failure.",
      barWidth: clamp(failed * 10, 8, 100),
      barClass: "red",
      iconClass: "red",
    },
    {
      id: "mismatch",
      title: "Name Mismatch",
      value: pct(mismatch),
      body: "Requires name normalization, school verification and parent confirmation.",
      barWidth: clamp(mismatch * 10, 8, 100),
      barClass: "purple",
      iconClass: "purple",
    },
    {
      id: "mbu",
      title: "MBU Pending",
      value: pct(mbu),
      body: "Largest operational queue; needs block-level ageing and follow-up.",
      barWidth: clamp(mbu * 4.5, 8, 100),
      barClass: "orange",
      iconClass: "orange",
    },
    {
      id: "not-provided",
      title: "Not Provided",
      value: pct(notProvided),
      body: "School-led data collection queue with missing-student follow-up.",
      barWidth: clamp(notProvided * 10, 8, 100),
      barClass: "",
      iconClass: "",
    },
  ];
};

export const buildCoverageLeaderboard = (blockData = [], limit = 8) =>
  [...blockData]
    .sort((a, b) => (b.aadhaar_coverage_pct ?? 0) - (a.aadhaar_coverage_pct ?? 0))
    .slice(0, limit)
    .map((b) => ({
      block: b.block_name,
      coverage: b.aadhaar_coverage_pct ?? 0,
    }));

/** Reference order: linked → failed → not provided → pending */
export const STATUS_DISTRIBUTION_ORDER = [
  {
    keys: ["Passed", "passed"],
    label: "Aadhaar Provided / Linked",
    color: "#10b981",
    dotClass: "",
  },
  {
    keys: ["Failed", "failed"],
    label: "Failed / Invalid",
    color: "#ef4444",
    dotClass: "red",
  },
  {
    keys: ["Not Provided", "Not provided", "not_provided"],
    label: "Not Provided",
    color: "#64748b",
    dotClass: "slate",
  },
  {
    keys: ["Pending", "pending"],
    label: "Pending",
    color: "#f59e0b",
    dotClass: "amber",
  },
];

export const buildStatusDistributionView = (distribution = []) => {
  const findRow = (keys) =>
    distribution.find((row) => keys.includes(row.name)) || {
      name: keys[0],
      value: 0,
      percentage: 0,
      color: STATUS_DISTRIBUTION_ORDER.find((m) => m.keys.some((k) => keys.includes(k)))?.color,
    };

  return STATUS_DISTRIBUTION_ORDER.map((meta) => {
    const row = findRow(meta.keys);
    return {
      name: meta.label,
      value: row.value ?? 0,
      percentage: row.percentage ?? 0,
      color: meta.color,
      dotClass: meta.dotClass,
    };
  });
};

export const buildStatusDonutStyle = (distribution = []) => {
  const items = buildStatusDistributionView(distribution);
  const segments = items.filter((item) => (item.percentage ?? 0) > 0);
  const donutItems = segments.length ? segments : items.slice(0, 3);

  let cursor = 0;
  const stops = donutItems.map((item) => {
    const pctVal = item.percentage ?? 0;
    const start = cursor;
    cursor += pctVal;
    return `${item.color} ${start}% ${cursor}%`;
  });

  if (cursor < 100 && stops.length) {
    stops.push(`${donutItems[donutItems.length - 1].color} ${cursor}% 100%`);
  }

  return {
    background: stops.length ? `conic-gradient(${stops.join(", ")})` : "conic-gradient(#e2e8f0 0 100%)",
    linkedPct: items[0]?.percentage ?? 0,
  };
};

const blockRiskStatus = (row) => {
  if (row.exceptionRate >= 7 || row.coverage < 94) return { label: "Critical", tone: "bad" };
  if (row.exceptionRate >= 5 || row.mbuPending >= 16) return { label: "High", tone: "warn" };
  return { label: "Watch", tone: "" };
};

export const buildBlockRiskReviewRows = (blockData = [], limit = 8) =>
  [...blockData]
    .map((b) => ({
      block: b.block_name,
      coverage: b.aadhaar_coverage_pct ?? 0,
      exceptionRate: b.aadhaar_exception_pct ?? b.exception_pct ?? 0,
      nameMismatch: b.name_mismatch_pct ?? 0,
      mbuPending: b.mbu_pending_pct ?? b.mbu_pending_total_pct ?? 0,
    }))
    .sort((a, b) => b.exceptionRate - a.exceptionRate || a.coverage - b.coverage)
    .slice(0, limit)
    .map((row, idx) => {
      const status = blockRiskStatus(row);
      return { priority: idx + 1, ...row, status: status.label, statusTone: status.tone };
    });

export const buildExecutiveActionQueueItems = (blockData = [], paretoData = []) => {
  const source = paretoData.length ? paretoData : [...blockData].sort((a, b) => (b.exceptions ?? 0) - (a.exceptions ?? 0));
  const top = source.slice(0, 4);
  const templates = [
    {
      iconClass: "red",
      priority: "Critical",
      priorityClass: "",
      title: (b) => `${b.block_name || b.block} exception cleanup sprint`,
      body: (b) =>
        `Highest exception contributor (${fmt(b.exceptions ?? 0)} records). Assign BEO and cluster coordinators for daily closure tracking.`,
    },
    {
      iconClass: "purple",
      priority: "High",
      priorityClass: "medium",
      title: () => "Name mismatch normalization",
      body: () => "Create school-wise mismatch list with parent-verification status and corrected-name workflow.",
    },
    {
      iconClass: "orange",
      priority: "High",
      priorityClass: "medium",
      title: () => "MBU pending ageing review",
      body: () => "Split cases into 0–7, 8–15 and 15+ days pending to remove bottlenecks.",
    },
    {
      iconClass: "",
      priority: "Medium",
      priorityClass: "low",
      title: () => "Not-provided recovery drive",
      body: () => "Generate school-wise missing Aadhaar list and schedule outreach through class teachers.",
    },
  ];
  return templates.map((tpl, i) => {
    const block = top[i] || top[0] || {};
    return {
      iconClass: tpl.iconClass,
      priority: tpl.priority,
      priorityClass: tpl.priorityClass,
      title: tpl.title(block),
      body: tpl.body(block),
    };
  });
};

export const buildPrimaryExecutiveAction = (blockData = []) => {
  const bottom = [...blockData]
    .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
    .slice(0, 4)
    .map((b) => b.block_name)
    .filter(Boolean);
  if (!bottom.length) {
    return "Run a 7-day Aadhaar closure sprint for bottom blocks and assign school-wise exception owners with daily progress review.";
  }
  return `Run a 7-day Aadhaar closure sprint for ${bottom.join(", ")} and assign school-wise exception owners with daily progress review.`;
};

export const buildActionWorkstreams = (overview = {}) => {
  const coverage = overview.aadhaar_coverage_pct ?? 0;
  const failed = overview.aadhaar_failed ?? 0;
  const mbu = overview.mbu_pending_total_pct ?? 0;
  const atRisk = overview.high_risk_schools ?? 0;
  const failedBar = clamp((failed / Math.max(overview.total_enrolment ?? 1, 1)) * 100 * 1.2, 12, 100);

  return [
    {
      id: "coverage",
      title: "Coverage Protection",
      value: coverage.toFixed(2),
      unit: "% coverage",
      priority: "Maintain",
      priorityClass: "green",
      iconClass: "green",
      barClass: "green",
      barWidth: clamp(coverage, 8, 100),
      owner: "Block MIS",
      cadence: "Daily",
    },
    {
      id: "mismatch",
      title: "Name Mismatch Closure",
      value: fmt(failed),
      unit: "failed",
      priority: "Priority 1",
      priorityClass: "red",
      iconClass: "red",
      barClass: "red",
      barWidth: failedBar,
      owner: "School HM",
      cadence: "7 days",
    },
    {
      id: "mbu",
      title: "MBU Pending Review",
      value: mbu.toFixed(2),
      unit: "% pending",
      priority: "Priority 2",
      priorityClass: "orange",
      iconClass: "purple",
      barClass: "purple",
      barWidth: clamp(mbu * 3.5, 12, 100),
      owner: "Cluster Officer",
      cadence: "Weekly",
    },
    {
      id: "governance",
      title: "Exception Governance",
      value: String(atRisk),
      unit: "schools >10%",
      priority: "Governance",
      priorityClass: "",
      iconClass: "orange",
      barClass: "orange",
      barWidth: atRisk > 0 ? clamp(atRisk * 4, 20, 100) : 100,
      owner: "District SPOC",
      cadence: "Review",
    },
  ];
};

export const buildBottomBlockLeaderboard = (blockData = [], bottomBlocks = [], limit = 10) => {
  const source =
    bottomBlocks.length > 0
      ? bottomBlocks.map((b) => ({
          block: b.block_name,
          schools: b.total_schools ?? 0,
          students: b.total_enrolment ?? 0,
          coverage: b.coverage_pct ?? b.aadhaar_coverage_pct ?? 0,
        }))
      : [...blockData]
          .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
          .map((b) => ({
            block: b.block_name,
            schools: b.total_schools ?? 0,
            students: b.total_enrolment ?? 0,
            coverage: b.aadhaar_coverage_pct ?? 0,
          }));

  return source.slice(0, limit).map((row, idx) => {
    const gap = clamp(100 - (row.coverage ?? 0), 0, 100);
    return {
      rank: idx + 1,
      block: row.block,
      schools: row.schools,
      students: row.students,
      coverage: row.coverage,
      gap,
      gapLabel: pct(gap),
      risk: idx < 3,
    };
  });
};

export const buildComplianceGapSummary = (overview = {}) => {
  const coverage = overview.aadhaar_coverage_pct ?? 0;
  const remaining = studentsToFullCoverage(overview);
  return {
    remaining,
    remainingLabel: fmt(remaining),
    coverage,
    coverageLabel: pct(coverage),
    covered: overview.aadhaar_passed ?? 0,
    coveredLabel: fmt(overview.aadhaar_passed ?? 0),
    failed: overview.aadhaar_failed ?? 0,
    pending: overview.aadhaar_pending ?? 0,
    notProvided: overview.aadhaar_not_provided ?? 0,
  };
};

const exceptionMixPriority = (block) => {
  const load =
    (block.aadhaar_failed_pct ?? 0) +
    (block.aadhaar_pending_pct ?? 0) +
    (block.mbu_pending_pct ?? 0);
  if (load >= 75) return "High";
  if (load >= 50) return "Med";
  return "Low";
};

export const buildExceptionMixBars = (blockData = [], limit = 6) => {
  const rows = [...blockData]
    .sort((a, b) => {
      const loadA =
        (a.aadhaar_failed ?? 0) + (a.aadhaar_pending ?? 0) + (a.mbu_pending_pct ?? 0);
      const loadB =
        (b.aadhaar_failed ?? 0) + (b.aadhaar_pending ?? 0) + (b.mbu_pending_pct ?? 0);
      return loadB - loadA;
    })
    .slice(0, limit);

  return rows.map((b) => {
    const failed = b.aadhaar_failed_pct ?? 0;
    const pending = b.aadhaar_pending_pct ?? 0;
    const mbu = b.mbu_pending_pct ?? 0;
    const total = failed + pending + mbu || 1;
    return {
      block: b.block_name,
      failedPct: (failed / total) * 100,
      pendingPct: (pending / total) * 100,
      mbuPct: (mbu / total) * 100,
      priority: exceptionMixPriority(b),
    };
  });
};

export const buildSprintTimeline = (overview = {}, bottomBlocks = [], highRiskSchools = []) => {
  const gap = studentsToFullCoverage(overview);
  const blockNames =
    bottomBlocks
      .slice(0, 3)
      .map((b) => b.block_name)
      .filter(Boolean)
      .join(", ") || "priority blocks";
  const riskSchools = highRiskSchools.length;
  const fullExceptionSchools = highRiskSchools.filter((s) => (s.exception_rate ?? 0) >= 100).length;
  const mbuCount = overview.mbu_pending_total ?? 0;
  const mismatchPct = overview.name_mismatch_pct ?? 0;

  return [
    {
      day: "D1",
      title: "Freeze Baseline and Assign Schools",
      body: `Generate school-wise exception list for ${bottomBlocks.length || "priority"} blocks (${blockNames}) covering ${fmt(gap)} remaining records and assign owner names.`,
      owner: "District SPOC",
    },
    {
      day: "D2",
      title: "Validate High-Risk Records",
      body: `Prioritize ${riskSchools} high-risk schools${fullExceptionSchools ? `, including ${fullExceptionSchools} at 100% exception rate` : ""}, with name mismatch at ${pct(mismatchPct)}.`,
      owner: "School HM",
    },
    {
      day: "D3",
      title: "Clear MBU Backlog",
      body: `Run batch status check for ${fmt(mbuCount)} MBU pending records (${pct(overview.mbu_pending_total_pct ?? 0)}) and escalate stuck batches.`,
      owner: "Block MIS",
    },
    {
      day: "D5",
      title: "Parent Outreach Drive",
      body: `Close not-provided cases (${pct(overview.aadhaar_not_provided_pct ?? overview.not_provided_pct ?? 0)}) using guardian contact, reason code and Aadhaar capture camp.`,
      owner: "Cluster Officer",
    },
    {
      day: "D7",
      title: "District Review and Escalation",
      body: `Review ${fmt(overview.aadhaar_exception_total ?? gap)} open exceptions, publish block leaderboard and escalate unresolved cases from ${blockNames}.`,
      owner: "District Admin",
    },
  ];
};

export const buildOwnerClosureModel = (overview = {}) => {
  const mismatch = overview.name_mismatch_pct ?? 0;
  const mbu = overview.mbu_pending_total_pct ?? 0;
  const notProvided = overview.aadhaar_not_provided_pct ?? overview.not_provided_pct ?? 0;
  return [
    {
      iconClass: "red",
      title: "Name Mismatch",
      body: `${pct(mismatch)} of enrolment (${fmt(overview.name_mismatch_total ?? 0)} records) — School HM verifies student details, uploads correction evidence, and submits exception closure to Block MIS.`,
    },
    {
      iconClass: "purple",
      title: "MBU Pending",
      body: `${pct(mbu)} workload (${fmt(overview.mbu_pending_total ?? 0)} students) — Cluster officer reviews backlog, validates batch status and escalates unresolved MBU records after 72 hours.`,
    },
    {
      iconClass: "orange",
      title: "Not Provided",
      body: `${pct(notProvided)} not-provided share — School outreach team contacts parent/guardian, records reason and completes data collection campaign.`,
    },
  ];
};

export const buildEscalationRules = (overview = {}, highRiskSchools = []) => {
  const openExceptions = overview.aadhaar_exception_total ?? studentsToFullCoverage(overview);
  return [
    {
      iconClass: "red",
      title: "Red: Unresolved Beyond 7 Days",
      body: `Escalate ${highRiskSchools.length} high-risk schools and ${fmt(openExceptions)} open exceptions to district officer with school name, student count, reason and owner.`,
    },
    {
      iconClass: "orange",
      title: "Amber: No Movement in 72 Hours",
      body: `Trigger block-level review when MBU backlog (${pct(overview.mbu_pending_total_pct ?? 0)}) or mismatch (${pct(overview.name_mismatch_pct ?? 0)}) shows no weekly progress.`,
    },
    {
      iconClass: "green",
      title: "Green: Closed and Verified",
      body: `Mark closed only after coverage impact is reflected at ${pct(overview.aadhaar_coverage_pct ?? 0)} and exception count is reconciled.`,
    },
  ];
};

export const buildEscalationDecisionNote = (blockData = [], bottomBlocks = []) => {
  const names = buildBottomBlockLeaderboard(blockData, bottomBlocks, 4).map((b) => b.block);
  if (!names.length) return "Prioritize lowest-coverage blocks first because they combine student volume with coverage gaps.";
  return `Prioritize ${names.join(", ")} first because they combine large student base with lower coverage.`;
};

const inferAccountabilityOwner = (school, rank) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 && enr <= 3) {
    if (rank === 1) return "School HM";
    if (rank === 2) return "Cluster Officer";
    return "Block MIS";
  }
  if (enr >= 100) return "District SPOC";
  if (rate >= 80 && enr <= 20) return "Cluster Officer";
  if (rate >= 75) return "School HM";
  return "Cluster Officer";
};

const inferAccountabilityAction = (school, rank) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 && enr <= 3) {
    if (rank === 1) return "Verify both records today";
    if (rank === 2) return "Parent outreach call";
    return "Check data upload status";
  }
  if (enr >= 100) return "Block-level escalation";
  if ((school.aadhaar_pending ?? 0) > (school.aadhaar_failed ?? 0)) return "Collect pending Aadhaar";
  if (rate >= 80) return "Name mismatch correction";
  if (enr <= 15) return "Fix Aadhaar";
  return "Document verification camp";
};

const inferAccountabilityStatus = (school) => {
  const rate = school.exception_rate ?? 0;
  const enr = school.total_enrolment ?? 0;
  if (rate >= 99 || (enr <= 3 && rate >= 80)) return { label: "Critical", tone: "red" };
  if (rate >= 75) return { label: "High", tone: "" };
  return { label: "Medium", tone: "green" };
};

export const buildPrioritySchoolQueue = (highRiskSchools = [], limit = 20) =>
  highRiskSchools.slice(0, limit).map((school, idx) => {
    const rank = idx + 1;
    const rate = school.exception_rate ?? 0;
    const status = inferAccountabilityStatus(school);
    return {
      rank,
      school: school.school_name || "—",
      udiseCode: school.udise_code,
      block: school.block_name || "—",
      blockCode: school.block_code,
      enrolment: school.total_enrolment ?? 0,
      exceptionRate: pct(rate, rate >= 99 ? 0 : 2),
      exceptionTone: rate >= 75 ? "red" : "",
      coverage: pct(school.aadhaar_coverage_pct ?? 0),
      owner: inferAccountabilityOwner(school, rank),
      nextAction: inferAccountabilityAction(school, rank),
      status: status.label,
      statusTone: status.tone,
    };
  });

export const buildAadhaarActionMarkdown = (
  overview = {},
  blockData = [],
  bottomBlocks = [],
  highRiskSchools = []
) => {
  const workstreams = buildActionWorkstreams(overview);
  const sprint = buildSprintTimeline(overview, bottomBlocks, highRiskSchools);
  const escalation = buildEscalationRules(overview, highRiskSchools);

  return [
    "## Insights",
    `- ${buildExecutiveHeroNarrative(overview)}`,
    `- Coverage ${pct(overview.aadhaar_coverage_pct ?? 0)} with ${fmt(Math.max(0, (overview.total_enrolment ?? 0) - (overview.aadhaar_passed ?? 0)))} students still to close.`,
    ...workstreams.slice(0, 3).map((w) => `- ${w.title}: ${w.value} ${w.unit} (${w.priority})`),
    "",
    "## Root Cause Signals",
    `- Name mismatch ${pct(overview.name_mismatch_pct ?? 0)} and MBU pending ${pct(overview.mbu_pending_total_pct ?? 0)}.`,
    `- ${overview.high_risk_schools ?? 0} schools above exception threshold.`,
    "",
    "## Recommendations",
    `- ${buildEscalationDecisionNote(blockData, bottomBlocks)}`,
    ...escalation.slice(0, 3).map((e) => `- ${e.title}: ${e.body}`),
    "",
    "## Priority Action Items",
    `- ${buildPrimaryExecutiveAction(blockData)}`,
    ...sprint.slice(0, 4).map((s) => `- ${s.title}: ${s.body}`),
  ].join("\n");
};

export const buildAadhaarInsightsMarkdown = (overview, blockData = [], highRiskSchools = []) => {
  const bottom = [...blockData]
    .sort((a, b) => (a.aadhaar_coverage_pct ?? 0) - (b.aadhaar_coverage_pct ?? 0))
    .slice(0, 3)
    .map((b) => b.block_name)
    .filter(Boolean);

  return [
    "## Insights",
    `- Aadhaar coverage is **${pct(overview?.aadhaar_coverage_pct ?? 0)}** across **${fmt(overview?.total_enrolment ?? 0)}** students.`,
    `- Exception rate is **${pct(overview?.aadhaar_exception_pct ?? 0)}** and name mismatch is **${pct(overview?.name_mismatch_pct ?? 0)}**.`,
    bottom.length ? `- Lowest coverage blocks: **${bottom.join(", ")}**.` : "",
    "",
    "## Root Cause Signals",
    ...buildAadhaarRootCauseSignals(overview).map((s) => `- ${s}`),
    "",
    "## Recommendations",
    ...buildAadhaarRecommendationBullets(overview, blockData).map((s) => `- ${s}`),
    "",
    "## Priority Action Items",
    ...buildAadhaarPriorityActions(overview, highRiskSchools).map((s) => `- ${s}`),
  ]
    .filter(Boolean)
    .join("\n");
};
